import {
    getSiteBuilderBlockDefinition,
    siteBuilderDropLinesForPlacement,
    siteBuilderPlacementAllowsDropLine,
    siteBuilderStyleSnapshotFromContext,
    siteBuilderWarningsForBlock,
    withSiteBuilderLayoutContainer,
    withSiteBuilderStyleSnapshot,
    SITE_BUILDER_EXTENDED_SLOT_OPTION,
    SITE_BUILDER_LAYOUT_CONTAINER_OPTION,
    SITE_BUILDER_SCHEMA_VERSION,
    SITE_BUILDER_STYLE_SNAPSHOT_OPTION,
    SITE_BUILDER_ZONE_OPTION,
    type SiteBuilderBlockCategory,
    type SiteBuilderBlockDefinition,
    type SiteBuilderDropLine,
    type SiteBuilderInspectorField,
    type SiteBuilderInspectorFieldKind,
    type SiteBuilderRendererDefinition,
    type SiteBuilderResolvedWarning,
    type SiteBuilderResponsiveRules,
    type SiteBuilderStyleSnapshot,
    type SiteBuilderWarningCode,
    type SiteBuilderWarningDefinition,
    type SiteBuilderZoneDefinition,
    type SiteBuilderZoneKind,
} from "./siteBuilderSchema";
import {
    getShopBlockLocalizationFields,
    type ShopBlockLocalizationFieldDescriptor,
} from "./localizationSchema";
import {
    getShopBlockDefinition,
    isShopDynamicEntityField,
    SHOP_DOCUMENT_ROLE_OPTION,
    SHOP_DYNAMIC_ENTITY_FIELD_OPTION,
    SHOP_GRID_SLOT_KEY,
} from "./shopBlocks";
import {
    CATALOG_PRODUCT_IMAGE_SLOT,
    CATALOG_PRODUCT_PAGE_DEFAULT_OPTIONS,
    CATALOG_PRODUCT_PAGE_TYPE,
    CATALOG_PRODUCT_SLOT,
} from "./catalogProductPage";
import {
    SITE_NAVIGATION_INSPECTOR_FIELDS,
    SITE_NAVIGATION_VARIANT_OPTION,
} from "./siteNavigation";
import { SHOP_NAVIGATION_VARIANTS } from "./shopVerticalCatalog";
import type { ShopTemplateBlock } from "./types";

export const SHOP_BUILDER_SCHEMA_VERSION = SITE_BUILDER_SCHEMA_VERSION;
export const SHOP_BUILDER_STYLE_SNAPSHOT_OPTION = SITE_BUILDER_STYLE_SNAPSHOT_OPTION;
export const SHOP_BUILDER_ZONE_OPTION = SITE_BUILDER_ZONE_OPTION;
export const SHOP_BUILDER_LAYOUT_CONTAINER_OPTION = SITE_BUILDER_LAYOUT_CONTAINER_OPTION;
export const SHOP_BUILDER_EXTENDED_SLOT_OPTION = SITE_BUILDER_EXTENDED_SLOT_OPTION;

export type ShopBuilderZoneKind = SiteBuilderZoneKind;
export type ShopBuilderDropLine = SiteBuilderDropLine;
export type ShopBuilderBlockCategory = SiteBuilderBlockCategory;
export type ShopBuilderInspectorFieldKind = SiteBuilderInspectorFieldKind;
export type ShopBuilderWarningCode = SiteBuilderWarningCode;
export type ShopBuilderInspectorField = SiteBuilderInspectorField;
export type ShopBuilderWarningDefinition = SiteBuilderWarningDefinition;
export type ShopBuilderResolvedWarning = SiteBuilderResolvedWarning;
export type ShopBuilderResponsiveRules = SiteBuilderResponsiveRules;
export type ShopBuilderRendererDefinition = SiteBuilderRendererDefinition;
export type ShopBuilderZoneDefinition = SiteBuilderZoneDefinition;
export type ShopBuilderStyleSnapshot = SiteBuilderStyleSnapshot;

export type ShopBuilderBlockDefinition = Omit<SiteBuilderBlockDefinition, "localization"> & {
    localization: readonly ShopBlockLocalizationFieldDescriptor[];
};

export type ShopBuilderPresetDefinition = {
    key: string;
    zones: ShopBuilderZoneDefinition[];
    allowStructureEditing: boolean;
};

const presetDefinitions = new Map<string, ShopBuilderPresetDefinition>();
const blockDefinitionCache = new Map<string, ShopBuilderBlockDefinition>();
const shopContentDefaults: Readonly<Record<string, Readonly<Record<string, unknown>>>> = {
    addToCartButton: {
        label: "Přidat do košíku",
    },
    blockquote: {
        author: "Tým obchodu",
        text: "\"Write a quote here from one of your customers. Quotes are a great way to build confidence in your products or services.\"",
    },
    searchBar: {
        buttonLabel: "Hledat",
        placeholder: "Hledat produkty",
    },
    supportedPaymentButton: {
        items: "Visa\nMastercard\nApple Pay\nGoogle Pay",
    },
};

const shopContentInspectorFields: Readonly<
    Record<string, readonly SiteBuilderInspectorField[]>
> = {
    searchBar: [
        { key: "placeholder", kind: "text", scope: "content" },
        { key: "buttonLabel", kind: "richText", scope: "content" },
    ],
};

function shopInspector(
    type: string,
    inspector: readonly SiteBuilderInspectorField[],
): SiteBuilderInspectorField[] {
    const contentFields = shopContentInspectorFields[type] ?? [];
    const adaptedInspector = [
        ...contentFields.filter((field) => !inspector.some(
            (candidate) => (
                candidate.key === field.key
                && candidate.scope === field.scope
            ),
        )),
        ...inspector,
    ];

    return adaptedInspector.map((field) => {
        if (
            type === "heading"
            && field.scope === "content"
            && field.key === "title"
        ) {
            return { ...field, key: "text" };
        }

        if (
            type === "icon"
            && field.scope === "content"
            && field.key === "href"
        ) {
            return { ...field, kind: "url" };
        }

        return field;
    });
}

function shopContextualContentDefaults(
    type: string,
    defaults: Readonly<Record<string, unknown>> | undefined,
): Record<string, unknown> {
    if (type !== "heading") {
        return { ...(defaults ?? {}) };
    }

    const {
        title: _legacyTitle,
        ...canonicalDefaults
    } = defaults ?? {};

    return {
        ...canonicalDefaults,
        text: "",
    };
}

const shopInspectorOptions: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
    form: {
        formAction: ["sendEmail", "createCustomer"],
    },
    searchBar: {
        searchWithin: ["everything", "pages", "products", "categories"],
    },
};

const shopAllDropLines: ShopBuilderDropLine[] = [
    "before",
    "after",
    "below",
    "left",
    "right",
    "inside",
];

function shopStructuralBlockDefinition(
    type: string,
    definition: SiteBuilderBlockDefinition,
): SiteBuilderBlockDefinition {
    const shopDefinition = getShopBlockDefinition(type);
    const definitionWithShopZones = (
        (shopDefinition?.slots?.length ?? 0) > 0
        && (
            (definition.zones?.length ?? 0) === 0
            || type === "footer"
        )
    )
        ? {
            ...definition,
            layoutContainer: true,
            zones: (shopDefinition?.slots ?? []).map((slot) => {
                const spatialGrid = slot.key === SHOP_GRID_SLOT_KEY;

                return {
                    accepts: [...slot.accepts],
                    capacity: slot.maxItems ?? 256,
                    dropLines: shopAllDropLines,
                    ...(spatialGrid ? { extendedRows: true } : {}),
                    key: slot.key,
                    kind: spatialGrid ? "grid" as const : "textFlow" as const,
                    textFlow: spatialGrid ? false : true,
                };
            }),
        }
        : definition;

    if (type === "richContent") {
        return {
            ...definitionWithShopZones,
            zones: definitionWithShopZones.zones?.map((zone) => (
                zone.key === "grid"
                    ? {
                        ...zone,
                        accepts: [
                            ...(zone.accepts ?? []),
                            ...((zone.accepts ?? []).includes(CATALOG_PRODUCT_PAGE_TYPE)
                                ? []
                                : [CATALOG_PRODUCT_PAGE_TYPE]),
                        ],
                    }
                    : zone
            )),
        };
    }

    if (type === "product") {
        return {
            ...definitionWithShopZones,
            layoutContainer: true,
            zones: [
                ...(definitionWithShopZones.zones ?? []).filter(
                    (zone) => zone.key !== CATALOG_PRODUCT_IMAGE_SLOT,
                ),
                {
                    key: CATALOG_PRODUCT_IMAGE_SLOT,
                    kind: "slot",
                    accepts: ["image"],
                    capacity: 1,
                    dropLines: shopAllDropLines,
                },
            ],
        };
    }

    if (type === "productRail") {
        return {
            ...definitionWithShopZones,
            category: "commerce",
            insertable: false,
        };
    }

    if (type === "systemCartSummary") {
        return {
            ...definitionWithShopZones,
            category: "commerce",
            insertable: false,
            systemOnly: true,
        };
    }

    if (type !== CATALOG_PRODUCT_PAGE_TYPE) {
        return definitionWithShopZones;
    }

    return {
        ...definition,
        category: "commerce",
        insertable: false,
        textFlow: false,
        standalone: false,
        layoutContainer: true,
        dropLines: ["before", "after", "below", "inside"],
        zones: [{
            key: CATALOG_PRODUCT_SLOT,
            kind: "slot",
            accepts: ["product"],
            capacity: 1,
            dropLines: shopAllDropLines,
        }],
        contextualDefaults: {
            ...(definition.contextualDefaults ?? {}),
            options: {
                ...CATALOG_PRODUCT_PAGE_DEFAULT_OPTIONS,
                ...(definition.contextualDefaults?.options ?? {}),
            },
        },
        inspector: [],
        localization: [],
        renderer: {
            component: CATALOG_PRODUCT_PAGE_TYPE,
            publicComponent: CATALOG_PRODUCT_PAGE_TYPE,
        },
        responsive: { stackOnMobile: true },
        styleCapabilities: ["layout", "responsive", "spacing"],
        warnings: [],
        systemOnly: true,
    };
}

export function registerShopBuilderPreset(definition: ShopBuilderPresetDefinition): void {
    presetDefinitions.set(definition.key, definition);
}

export function getShopBuilderPresetDefinition(
    key: string | null | undefined,
): ShopBuilderPresetDefinition | null {
    return key ? presetDefinitions.get(key) ?? null : null;
}

export function getShopBuilderBlockDefinition(type: string): ShopBuilderBlockDefinition {
    const cached = blockDefinitionCache.get(type);

    if (cached) {
        return cached;
    }

    const definition = shopStructuralBlockDefinition(
        type,
        getSiteBuilderBlockDefinition(type),
    );
    const contentDefaults = shopContentDefaults[type];
    const inspectorOptions = shopInspectorOptions[type];
    const contextualContentDefaults = shopContextualContentDefaults(
        type,
        definition.contextualDefaults?.content,
    );
    const inspector = shopInspector(type, definition.inspector);

    const adaptedDefinition: ShopBuilderBlockDefinition = {
        ...definition,
        ...((contentDefaults || type === "heading") ? {
            contextualDefaults: {
                ...(definition.contextualDefaults ?? {}),
                content: {
                    ...contextualContentDefaults,
                    ...(contentDefaults ?? {}),
                },
            },
        } : {}),
        ...(inspectorOptions ? {
            inspector: inspector.map((field) => (
                inspectorOptions[field.key]
                    ? { ...field, options: [...inspectorOptions[field.key]] }
                    : field
            )),
        } : { inspector }),
        localization: getShopBlockLocalizationFields(type),
    };

    blockDefinitionCache.set(type, adaptedDefinition);

    return adaptedDefinition;
}

const shopDocumentRolePresentationTypes: Readonly<Record<string, string>> = {
    promoContent: "promoBar",
    headerLogo: "headerLogo",
    headerMenu: "headerMenu",
    headerSearch: "headerSearch",
    headerStoresButton: "headerStoresButton",
    headerAccountButton: "headerAccountButton",
    headerLanguageSelector: "headerLanguageSelector",
    headerCurrencySelector: "headerCurrencySelector",
    headerFavoritesButton: "headerFavoritesButton",
    headerCartButton: "headerCartButton",
    footerService: "footerService",
    footerHelp: "footerLinks",
    footerCompany: "footerLinks",
    footerLinks: "footerLinks",
    footerSocial: "footerSocial",
    footerApps: "footerApps",
    footerBottom: "footerCopyright",
    footerCopyright: "footerCopyright",
};

function shopBlockUsesRuntimeText(block: ShopTemplateBlock): boolean {
    if (
        block.type !== "heading"
        && block.type !== "paragraph"
        && block.type !== "text"
    ) {
        return false;
    }

    const runtimeText = block.options?.runtimeText;
    const dynamicEntityField =
        block.options?.[SHOP_DYNAMIC_ENTITY_FIELD_OPTION];

    return (
        (
            typeof runtimeText === "string"
            || typeof runtimeText === "number"
        )
        && String(runtimeText) !== ""
    ) || isShopDynamicEntityField(dynamicEntityField);
}

function shopBlockUsesRuntimeLink(block: ShopTemplateBlock): boolean {
    const runtimeRole = block.options?.commerceRuntimeRole;

    return typeof runtimeRole === "string" && runtimeRole !== "";
}

/**
 * Resolves inspector-only presentation from one concrete Shop block instance.
 *
 * Persisted blocks stay neutral (`column`, `searchBar`, `text`, ...). A
 * document role only improves the selected scope label; all fields, zones and
 * placement behavior continue to come from the persisted neutral type.
 */
export function getShopBuilderBlockInspectorDefinition(
    block: ShopTemplateBlock,
): ShopBuilderBlockDefinition {
    const definition = getShopBuilderBlockDefinition(block.type);
    const documentRole = block.options?.[SHOP_DOCUMENT_ROLE_OPTION];
    const presentationType = (
        block.type === "column"
        && typeof documentRole === "string"
    )
        ? shopDocumentRolePresentationTypes[documentRole]
        : undefined;
    const runtimeText = shopBlockUsesRuntimeText(block);
    const runtimeLink = shopBlockUsesRuntimeLink(block);
    const runtimeProduct = (
        block.type === "product"
        && documentRole === "productCardTemplate"
    );
    const runtimeProductImage = (
        block.type === "image"
        && documentRole === "productImage"
    );
    const baseInspector = runtimeText || runtimeLink || runtimeProduct || runtimeProductImage
        ? definition.inspector.filter((field) => !(
            field.scope === "content" && (
                (
                    runtimeText
                    && (field.key === "text" || field.key === "title")
                )
                || (runtimeLink && field.key === "href")
                || runtimeProduct
                || (
                    runtimeProductImage
                    && (field.kind === "media" || field.key === "alt")
                )
            )
        ))
        : definition.inspector;
    const navigationInspector = [
        ...SITE_NAVIGATION_INSPECTOR_FIELDS.filter(
            (field) => field.key !== SITE_NAVIGATION_VARIANT_OPTION,
        ),
        ...SITE_NAVIGATION_INSPECTOR_FIELDS.filter(
            (field) => field.key === SITE_NAVIGATION_VARIANT_OPTION,
        ),
    ].map(({ options, ...field }) => ({
        ...field,
        ...(field.key === SITE_NAVIGATION_VARIANT_OPTION
            ? { options: [...SHOP_NAVIGATION_VARIANTS] }
            : options
                ? { options: [...options] }
                : {}),
    }));
    const inspector = presentationType === "headerMenu"
        ? [...baseInspector, ...navigationInspector]
        : baseInspector;
    const localization = runtimeProduct
        ? []
        : runtimeProductImage
            ? definition.localization.filter(
                (field) => field.fieldCode !== "content.alt",
            )
            : definition.localization;
    const warnings = runtimeProductImage
        ? definition.warnings.filter((warning) => warning.code !== "missingMedia")
        : definition.warnings;

    if (
        !presentationType
        && inspector === definition.inspector
        && localization === definition.localization
        && warnings === definition.warnings
    ) {
        return definition;
    }

    return {
        ...definition,
        inspector: [...inspector],
        localization: [...localization],
        type: presentationType ?? definition.type,
        warnings: [...warnings],
    };
}

export function shopBuilderDropLinesForBlock(
    block: ShopTemplateBlock | string,
): ShopBuilderDropLine[] {
    return getShopBuilderBlockDefinition(
        typeof block === "string" ? block : block.type,
    ).dropLines;
}

export const shopBuilderDropLinesForPlacement = siteBuilderDropLinesForPlacement;

export function shopBuilderBlockAllowsDropLine(
    block: ShopTemplateBlock | string,
    line: ShopBuilderDropLine,
): boolean {
    return shopBuilderDropLinesForBlock(block).includes(line);
}

export const shopBuilderPlacementAllowsDropLine = siteBuilderPlacementAllowsDropLine;

export function shopBuilderBlockCreatesLayoutOnDrop(
    block: ShopTemplateBlock | string,
    line: ShopBuilderDropLine,
): boolean {
    const definition = getShopBuilderBlockDefinition(
        typeof block === "string" ? block : block.type,
    );

    return line === "left" || line === "right" || line === "below" || definition.layoutContainer;
}

export function shopBuilderBlockIsTextFlow(
    block: ShopTemplateBlock | string,
): boolean {
    return getShopBuilderBlockDefinition(
        typeof block === "string" ? block : block.type,
    ).textFlow;
}

export function shopBuilderBlockIsLayoutContainer(
    block: ShopTemplateBlock | string,
): boolean {
    return getShopBuilderBlockDefinition(
        typeof block === "string" ? block : block.type,
    ).layoutContainer;
}

export const shopBuilderStyleSnapshotFromContext = siteBuilderStyleSnapshotFromContext;
export const withShopBuilderStyleSnapshot = withSiteBuilderStyleSnapshot;
export const withShopBuilderLayoutContainer = withSiteBuilderLayoutContainer;

export function shopBuilderWarningsForBlock(
    block: ShopTemplateBlock,
): ShopBuilderResolvedWarning[] {
    const contextualWarnings = getShopBuilderBlockInspectorDefinition(block).warnings;

    return siteBuilderWarningsForBlock(block).filter((warning) => (
        contextualWarnings.some((candidate) => (
            candidate.code === warning.code
            && candidate.field === warning.field
        ))
    ));
}
